import type { UseFormReturn } from 'react-hook-form';

import dayjs from 'dayjs';
import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import type { PaymentTerm } from '~/api/common/types';

import { Field, Select, Switch } from '~/components/Form';
import { includes } from '~/utils/arrays';

import { rowStyle, selectTriggerWrapperStyle } from './FormPaymentTerm.css';

type FormWithPaymentTerm = {
  paymentTerm: PaymentTerm;
  paymentTermEndOfMonth: boolean;
};

type Props<T extends FormWithPaymentTerm> = {
  form: T extends FormWithPaymentTerm ? UseFormReturn<T> : never;
  helpText?: 'invoice' | 'invoiceTemplate';
};

export const FormPaymentTerm = <T extends FormWithPaymentTerm>({ form: { control, watch }, helpText }: Props<T>) => {
  const { t } = useTranslation(['invoices', 'invoiceTemplates', 'validation']);

  const watchPaymentTerm = watch('paymentTerm');
  const watchPaymentTermEndOfMonth = watch('paymentTermEndOfMonth');

  const paymentTermItems = ([15, 30, 45, 60] satisfies PaymentTerm[]).map((paymentTerm) => ({
    value: paymentTerm,
    label: t(`invoices:fields.billingPeriod.options.${paymentTerm}`),
  }));

  return (
    <Controller
      control={control}
      name="paymentTerm"
      render={({ field: { name, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
        <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
          <Field.Label>{t('invoices:fields.billingPeriod.label')}</Field.Label>

          <div className={rowStyle}>
            <Select.Root items={paymentTermItems} onValueChange={(next) => next && onChange(next)} value={value}>
              <div className={selectTriggerWrapperStyle}>
                <Select.Button ref={ref} />
              </div>

              <Select.List>
                {paymentTermItems.map((option) => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.List>
            </Select.Root>

            {!includes([45, 60] satisfies PaymentTerm[], watchPaymentTerm) && (
              <Controller
                control={control}
                name="paymentTermEndOfMonth"
                render={({ field: { onChange: onToggle, ref: toggleRef, value: endOfMonth } }) => (
                  <Field.Root>
                    <Switch.Root checked={endOfMonth} onCheckedChange={onToggle} ref={toggleRef}>
                      <Switch.Label>{t('invoices:fields.billingPeriodEndOfMonth.label')}</Switch.Label>
                    </Switch.Root>
                  </Field.Root>
                )}
              />
            )}
          </div>

          <Field.Footer>
            {helpText && (
              <Field.Description>
                {match(helpText)
                  .with('invoice', () => (
                    <Trans
                      components={[<strong key={null} />]}
                      i18nKey="invoices:fields.billingPeriod.helpText"
                      values={{
                        date: dayjs()
                          .endOf(watchPaymentTermEndOfMonth ? 'month' : 'millisecond')
                          .add(watchPaymentTerm, 'days')
                          .format('DD/MM/YYYY'),
                      }}
                    />
                  ))
                  .with('invoiceTemplate', () =>
                    watchPaymentTermEndOfMonth
                      ? t('invoiceTemplates:createEdit.fields.paymentTerm.helpTextEndOfMonth', { days: watchPaymentTerm })
                      : t('invoiceTemplates:createEdit.fields.paymentTerm.helpText', { days: watchPaymentTerm }),
                  )
                  .exhaustive()}
              </Field.Description>
            )}

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Footer>
        </Field.Root>
      )}
      rules={{ required: { value: true, message: t('validation:required') } }}
    />
  );
};
